<template>
  <div class="c-domain-setup-form pt-4">
    <div>
      <ListView @open-integration="handleIntegrationOpen"/>
    </div>

    <div class="mt-16 mb-12 flex justify-center text-sm">
      <strong>Add more <i class="el-icon-bottom"></i></strong>
    </div>

    <div v-for="integration in integrationsConfig.slice(0, 3)" :key="integration.id" class="w-full p-3 flex items-center gap-6">
      <div class="w-40 flex-shrink-0">
        <img :src="integration.logo" :alt="`${integration.name} Logo`" class="m-auto"/>
      </div>

      <div class="w-full flex justify-between">
        <div class="max-w-md flex flex-col gap-y-2">
          <p class="text-lg font-semibold">{{ integration.description }}</p>
          <a :href="integration.documentationUrl" class="underline text-sm" target="_blank">
            Learn more about {{ integration.name }}
          </a>
        </div>

        <div>
          <button
              v-if="!isIntegrationPresent(integration.id)"
              class="btn secondary outline medium m-auto mb-2"
              type="button"
              :disabled="isIntegrationPresent(integration.id)"
              @click.stop="handleIntegrationOpen('', integration.id)">
            {{ integration.requiresOAuth ? `Connect to ${integration.name}` : `Add ${integration.name}` }}
          </button>
          <span v-else>
            Active
          </span>
        </div>
      </div>
    </div>

    <!-- More Integrations -->
    <div class="w-full pt-6 flex items-center gap-2">
      <div class="w-full flex items-center justify-center">
        <button
            class="btn secondary outline large m-auto mb-2"
            type="button"
            @click.stop="toggleIntegrationsList">
          Browse More Integrations
        </button>
      </div>
    </div>

    <IntegrationsListModal v-if="integrationsListVisible" @close="triggerModalClose"/>

    <el-drawer
        :size="'70%'"
        class="c-drawer"
        :visible.sync="drawerVisible"
        :before-close="handleClose"
        :destroy-on-close="true">
      <div slot="title" class="mb-0 text-body-black">
        <strong>{{ targetIntegration ? `${targetIntegration} Integration` : 'New Integration' }}</strong>
        <hr class="mt-2"/>
      </div>

      <component
          :is="currentIntegrationComponent"
          @form-modified="childFormModified = $event"
          @close-drawer="triggerDrawerClose"
          @force-close="triggerForceClose"/>
    </el-drawer>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import ListView from '@components/integrations/ListView.vue'
import IntegrationsListModal from '@components/modals/IntegrationsListModal.vue'
import { INTEGRATIONS } from '@meta/integrations';

export default {
  name: 'Integrations',
  components: {
    ListView,
    IntegrationsListModal,
  },
  data() {
    return {
      drawerVisible: false,
      integrationsListVisible: false,
      targetIntegration: null,
      childFormModified: false,
      integrationsConfig: INTEGRATIONS,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID']),
    ...mapGetters('integration', ['isIntegrationPresent']),
    targetWidgetID() {
      return this.$store.state.campaign.widget.targetWidgetID
    },
    currentIntegrationComponent() {
      const integration = this.integrationsConfig.find(
          (int) => int.id === this.targetIntegration,
      )
      if (!integration) return null

      // Return an asynchronous component using dynamic import
      return () => import(`@components/forms/integrations/${integration.component}.vue`)
    },
  },
  mounted() {
    window.addEventListener('message', this.receiveMessage, false)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.receiveMessage, false)
  },
  methods: {
    ...mapActions('integration', [
      'setTargetIntegration',
      'listIntegrations',
      'initiateOAuthIntegration',
      'handleOAuthCallback',
    ]),
    /**
     * Handles the opening of integrations.
     * @param {String} id - The ID of the integration. Empty string for new integrations.
     * @param {String} targetIntegration - The target integration type (e.g., 'MAILCHIMP').
     */
    handleIntegrationOpen(id = '', targetIntegration) {
      if (id) {
        // Existing integration: open the drawer with integration details
        this.setTargetIntegration(id)
        this.targetIntegration = targetIntegration
        this.drawerVisible = true
      } else {
        // New integration: check if it requires OAuth
        const integration = this.integrationsConfig.find(
            (int) => int.id === targetIntegration,
        )

        if (integration && integration.requiresOAuth) {
          this.initiateOAuthIntegration({
            widgetId: this.targetWidgetID,
            integrationType: integration.id,
          })
        } else {
          // Non-OAuth integration: open the drawer for setup
          this.setTargetIntegration('')
          this.targetIntegration = targetIntegration
          this.drawerVisible = true
        }
      }
    },
    handleClose(done) {
      if (this.childFormModified) {
        this.$msgbox
            .confirm('You have unsaved changes. Are you sure you want to exit?')
            .then(() => {
              done()
              this.childFormModified = false
            })
            .catch(() => {})
      } else {
        done()
      }
    },
    triggerDrawerClose() {
      this.handleClose(() => {
        this.drawerVisible = false
        this.targetIntegration = null
      })
    },
    triggerModalClose(name) {
      this.integrationsListVisible = false
      if (name) {
        this.handleIntegrationOpen('', name)
      }
    },
    triggerForceClose() {
      this.drawerVisible = false
      this.childFormModified = false
    },
    toggleIntegrationsList() {
      this.integrationsListVisible = !this.integrationsListVisible
    },
    receiveMessage(event) {
      // For security, verify the origin of the message
      const expectedOrigin = window.location.origin

      if (event.origin !== expectedOrigin) {
        console.warn('Received message from unauthorized origin:', event.origin)
        return
      }

      const data = event.data
      const { type, integrationType, integrationId } = data

      if (type && integrationType && integrationId) {
        this.$store.dispatch('integration/handleOAuthCallback', data)
        this.targetIntegration = integrationType.toUpperCase()
        this.drawerVisible = true
      }
    },
  },
}
</script>

<style lang="scss">
.c-drawer {
  .el-drawer__header {
    @apply mb-0;
  }

  .el-drawer__body {
    @apply pt-6 overflow-y-auto;
  }
}
</style>